import { useAntdTable, useRequest } from 'ahooks'
import { Button, DatePicker, Form, Input, InputNumber, Modal, Radio, Select, Steps, message } from 'antd'
import { ButtonType } from 'antd/es/button/'
import { produce } from 'immer'
import { useAtom } from 'jotai'
import { differenceBy, isNil, omit, omitBy, pickBy, toNumber } from 'lodash'
import moment from 'moment'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Editor, UsersSearch } from '@/components'
import { NormalResourceTable, deleteChildren } from '@/modules/portfolio/portfolio-list'
import { userInfoAtom } from '@/modules/user'
import { FetchType, PpRelation, PriorityEnum, ResourceType } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { Production, ProductionFlowStatus, ProductionRole, ValidateNotPassReason } from '../production.schema'

interface ActionButtonBase {
  production: Production & { productionVps: string[] }
  refresh: () => void
  ipdCommittee: string[]
  financialOperationAdmin: string[]
  buttonType?: ButtonType
}

const FlowStatusCloseButton: React.FC<{
  production: Production & { productionVps: string[] }
  refresh: () => void
  buttonType?: ButtonType
}> = ({ refresh, production, buttonType = 'link' }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [step, setStep] = React.useState(0)

  const [closeVisible, setCloseVisible] = React.useState(false)
  const [closeForm] = Form.useForm()

  const isOwner = (production.roles || []).some(
    (r) => [ProductionRole.负责人, ProductionRole.产品经理].includes(r.role as any) && r.email === viewer.email
  )
  const isSuperAdmin = viewer.isSuperAdmin
  const shouldDisplayCloseButton = isOwner || isSuperAdmin

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setCloseVisible(false)
      },
      onSuccess() {
        refresh()
        setCloseVisible(false)
      },
    }
  )

  return (
    <>
      {shouldDisplayCloseButton && (
        <Button loading={loading} danger onClick={() => setCloseVisible(true)} type={buttonType} size='small'>
          {t('production.flowStatus.DevOnGoing.close.button')}
        </Button>
      )}
      <Modal
        width={1000}
        title={
          <Steps current={step} onChange={(step) => setStep(step)} className='pr-4'>
            <Steps.Step title={t('final.step.warning.title')} />
            <Steps.Step title={t('production.flowStatus.DevOnGoing.close.button')} />
          </Steps>
        }
        visible={closeVisible}
        destroyOnClose
        onOk={async () => {
          if (step === 0) {
            setStep(1)
          } else {
            const values = await closeForm.validateFields()
            run({ ...values, action: 'close' })
          }
        }}
        onCancel={() => {
          setCloseVisible(false)
        }}
        okButtonProps={{ loading, danger: step === 1 }}
        cancelButtonProps={{
          loading,
          disabled: step === 0,
          onClick: () => {
            if (step === 1) {
              setStep(0)
            }
          },
        }}
        okText={step === 0 ? t('next.step') : t('confirm')}
        cancelText={t('previous.step')}
      >
        {step === 0 ? (
          <FinalActionResourceTable
            production={production}
            refresh={refresh}
            actionStatus={t('production.flowStatus.termination.close')}
          />
        ) : null}
        {step === 1 ? (
          <Form form={closeForm} layout='vertical'>
            <Form.Item
              label={t('production.flowStatus.DevOnGoing.close.form.title')}
              name='closeReason'
              rules={[{ required: true, whitespace: true }]}
            >
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </>
  )
}

export const FlowStatusButton: React.FC<ActionButtonBase> = ({
  production,
  refresh,
  ipdCommittee,
  financialOperationAdmin,
  buttonType = 'link',
}) => {
  const confirmedTargetCompleteTime = production.flows
    ?.find((f) => f.status === ProductionFlowStatus.待项目结项)
    ?.additions.find((a) => a.type === 'confirmedTargetCompleteTime')?.value

  const button = (() => {
    switch (production.flowStatus) {
      case ProductionFlowStatus.待立项申请: {
        return (
          <ProjectWaitForApplyActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.待指派TO: {
        return (
          <TOWaitForAssignActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.待资源预估: {
        return (
          <ResourceWaitForEstimateActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.待立项审批: {
        return (
          <ProjectWaitForAuditActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.待IPD审批: {
        return (
          <IPDWaitForAuditActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.研发进行中: {
        return (
          <DevOnGoingActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.待产品验收: {
        return (
          <ProductWaitForValidateActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.待项目结项: {
        return (
          <ProjectWaitForFinishActionButton
            production={production}
            refresh={refresh}
            ipdCommittee={ipdCommittee}
            financialOperationAdmin={financialOperationAdmin}
            buttonType={buttonType}
          />
        )
      }
      case ProductionFlowStatus.项目已结项: {
        return null
      }
      case ProductionFlowStatus.项目已关闭: {
        return null
      }
      case ProductionFlowStatus.项目已驳回: {
        return null
      }
      default: {
        return null
      }
    }
  })()

  // 独立于状态外，根据 confirmedTargetCompleteTime 判断是否展示
  const resultTracking =
    confirmedTargetCompleteTime &&
    moment().isSameOrAfter(moment(confirmedTargetCompleteTime), 'day') &&
    isNil(production.trackingResult) ? (
      <TrackingResultActionButton
        production={production}
        refresh={refresh}
        ipdCommittee={ipdCommittee}
        financialOperationAdmin={financialOperationAdmin}
        buttonType={buttonType}
      />
    ) : null
  return (
    <>
      {resultTracking}
      {button}
    </>
  )
}

export const ProjectWaitForApplyActionButton: React.FC<ActionButtonBase> = ({ production, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const isProductManager = (production.roles || []).some(
    (r) => [ProductionRole.产品经理, ProductionRole.负责人].includes(r.role as any) && r.email === viewer.email
  )
  const isSuperAdmin = viewer.isSuperAdmin

  const { run, loading } = useRequest(
    () => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {},
      onSuccess() {
        refresh()
      },
    }
  )

  return (
    <>
      {!isProductManager && !isSuperAdmin ? null : (
        <Button loading={loading} type={buttonType} size='small' onClick={() => run()}>
          {t('production.flowStatus.ProjectWaitForApply.button')}
        </Button>
      )}
      <FlowStatusCloseButton buttonType={buttonType} production={production} refresh={refresh} />
    </>
  )
}

export const TOWaitForAssignActionButton: React.FC<ActionButtonBase> = ({ production, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const isSuperAdmin = viewer.isSuperAdmin
  const isCommittee = viewer.roles.find((r) => r.role === `${ResourceType.PRODUCTION}_committee`)
  const shouldDisabled = !isCommittee && !isSuperAdmin

  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  return (
    <>
      {!shouldDisabled ? (
        <>
          <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
            {t('production.flowStatus.TOWaitForAssign.button')}
          </Button>
          <Modal
            title={t('production.flowStatus.TOWaitForAssign.button')}
            visible={visible}
            destroyOnClose
            onCancel={() => setVisible(false)}
            onOk={async () => {
              const values = await form.validateFields()
              run(values)
            }}
            okText={t('save')}
            okButtonProps={{ loading }}
            cancelButtonProps={{ disabled: loading }}
          >
            <Form form={form} layout='vertical'>
              <Form.Item label={t('production.TO')} name='TO' rules={[{ required: true }]}>
                <UsersSearch placeholder={t('production.TO')} />
              </Form.Item>
            </Form>
          </Modal>
        </>
      ) : null}
      <FlowStatusCloseButton buttonType={buttonType} production={production} refresh={refresh} />
    </>
  )
}

export const ResourceWaitForEstimateActionButton: React.FC<ActionButtonBase> = ({
  production,
  refresh,
  buttonType,
}) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const isSuperAdmin = viewer.isSuperAdmin
  const isTO = (production.roles || []).some((r) => r.role === ProductionRole.TO && r.email === viewer.email)
  const stages = production.additions.filter((a) => a.type === 'Stage')

  const [stagePredictWorkload, setStagePredictWorkload] = React.useState({})

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  const onValuesChange = (data: any) => {
    setStagePredictWorkload({ ...stagePredictWorkload, ...data })
  }

  return (
    <>
      {!isSuperAdmin && !isTO ? null : (
        <>
          <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
            {t('production.flowStatus.ResourceWaitForEstimate.button')}
          </Button>
          <Modal
            title={t('production.flowStatus.ResourceWaitForEstimate.button')}
            visible={visible}
            destroyOnClose
            onCancel={() => setVisible(false)}
            onOk={async () => {
              const values = await form.validateFields()
              run({ predictWorkload: JSON.stringify(values) })
            }}
            okText={t('save')}
            okButtonProps={{ loading }}
            cancelButtonProps={{ disabled: loading }}
          >
            <div className='whitespace-pre-wrap text-red-500 mb-4'>
              {t('production.flowStatus.ResourceWaitForEstimate.form.note')}:
              <div>
                1.
                <Trans
                  i18nKey='production.flowStatus.ResourceWaitForEstimate.form.note1'
                  components={{
                    Bold: <span style={{ fontWeight: 'bold' }} />,
                  }}
                ></Trans>
              </div>
              <div>
                2.
                <Trans
                  i18nKey='production.flowStatus.ResourceWaitForEstimate.form.note2'
                  components={{
                    Bold: <span style={{ fontWeight: 'bold' }} />,
                  }}
                ></Trans>
              </div>
            </div>
            <Form form={form} layout='horizontal' onValuesChange={onValuesChange}>
              {stages.map((stage, index) => (
                <Form.Item
                  label={t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadStage', {
                    index: index + 1,
                  })}
                  key={stage.id}
                  name={`${stage.type}.${index}`}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} precision={0} />
                </Form.Item>
              ))}
            </Form>
            <div className='font-bold mt-2'>
              {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTotal')}:{' '}
              {(Object.values(stagePredictWorkload) as number[]).reduce((pre, current) => pre + current, 0)}
            </div>
          </Modal>
        </>
      )}
      <FlowStatusCloseButton buttonType={buttonType} production={production} refresh={refresh} />
    </>
  )
}

export const ProjectWaitForAuditActionButton: React.FC<ActionButtonBase> = ({ production, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const isProductVP = (production.productionVps || []).includes(viewer.email)
  const isSuperAdmin = viewer.isSuperAdmin

  const [visible, setVisible] = React.useState<'pass' | 'reject' | undefined>()

  const [form] = Form.useForm()

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(undefined)
      },
      onSuccess() {
        refresh()
        setVisible(undefined)
      },
    }
  )

  return (
    <>
      {!isSuperAdmin && !isProductVP ? null : (
        <>
          <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible('pass')}>
            {t('production.flowStatus.ProjectWaitForAudit.pass.button')}
          </Button>
          <Button loading={loading} danger type={buttonType} size='small' onClick={() => setVisible('reject')}>
            {t('production.flowStatus.ProjectWaitForAudit.reject.button')}
          </Button>
          <Modal
            title={t(`production.flowStatus.ProjectWaitForAudit.${visible}.button`)}
            visible={!!visible}
            destroyOnClose
            onCancel={() => setVisible(undefined)}
            onOk={async () => {
              const values = await form.validateFields()
              run({ ...values, action: visible })
            }}
            okText={t('save')}
            okButtonProps={{ loading }}
            cancelButtonProps={{ disabled: loading }}
          >
            <Form
              form={form}
              initialValues={{ ...(visible === 'pass' ? { priority: production.priority } : {}) }}
              layout='vertical'
            >
              {visible === 'reject' ? (
                <Form.Item
                  label={t('production.flowStatus.ProjectWaitForAudit.reject.reason')}
                  name='rejectReason'
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                </Form.Item>
              ) : (
                <Form.Item
                  label={t('production.detail.attribute')}
                  required
                  name='priority'
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder={t('production.detail.priority')}
                    options={Object.keys(PriorityEnum)
                      .filter((v) => isNaN(toNumber(v)))
                      .map((key) => {
                        const value = PriorityEnum[key]
                        return {
                          value,
                          label: t(`form.priority.${key}`),
                        }
                      })}
                  ></Select>
                </Form.Item>
              )}
            </Form>
          </Modal>
        </>
      )}
      <FlowStatusCloseButton buttonType={buttonType} production={production} refresh={refresh} />
    </>
  )
}

export const IPDWaitForAuditActionButton: React.FC<ActionButtonBase> = ({
  production,
  refresh,
  ipdCommittee,
  buttonType,
}) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const isIPDCommittee = ipdCommittee.includes(viewer.email)
  const isSuperAdmin = viewer.isSuperAdmin

  const shouldDisplayIPDButton = isIPDCommittee || isSuperAdmin

  const [rejectVisible, setRejectVisible] = React.useState(false)
  const [passVisible, setPassVisible] = React.useState(false)

  const [rejectForm] = Form.useForm()
  const [passForm] = Form.useForm()

  React.useEffect(() => {
    passForm.setFieldsValue({ priority: production.priority })
  }, [production])

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setRejectVisible(false)
        setPassVisible(false)
      },
      onSuccess() {
        refresh()
        setRejectVisible(false)
        setPassVisible(false)
      },
    }
  )

  return (
    <>
      {shouldDisplayIPDButton && (
        <Button loading={loading} type={buttonType} size='small' onClick={() => setPassVisible(true)}>
          {t('production.flowStatus.IPDWaitForAudit.IPDPass.button')}
        </Button>
      )}
      {shouldDisplayIPDButton && (
        <Button loading={loading} danger type={buttonType} size='small' onClick={() => setRejectVisible(true)}>
          {t('production.flowStatus.IPDWaitForAudit.IPDReject.button')}
        </Button>
      )}
      <Modal
        visible={rejectVisible}
        destroyOnClose
        okText={t('reject')}
        onCancel={() => setRejectVisible(false)}
        onOk={async () => {
          const values = await rejectForm.validateFields()
          run({ ...values, action: 'IPDReject' })
        }}
        okButtonProps={{ danger: true, loading }}
        cancelButtonProps={{ disabled: loading }}
      >
        <Form form={rejectForm} layout='vertical'>
          <Form.Item
            label={t('production.flowStatus.IPDWaitForAudit.IPDReject.form.title')}
            name='rejectReason'
            rules={[{ required: true, whitespace: true }]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        visible={passVisible}
        destroyOnClose
        onCancel={() => setPassVisible(false)}
        onOk={async () => {
          const values = await passForm.validateFields()
          run({ ...values, action: 'IPDPass' })
        }}
      >
        <Form form={passForm} layout='vertical'>
          <Form.Item label={t('production.detail.attribute')} required name='priority' rules={[{ required: true }]}>
            <Select
              placeholder={t('production.detail.attribute')}
              options={Object.keys(PriorityEnum)
                .filter((v) => isNaN(toNumber(v)))
                .map((key) => {
                  const value = PriorityEnum[key]
                  return {
                    value,
                    label: t(`form.priority.${key}`),
                  }
                })}
            ></Select>
          </Form.Item>
          <Form.Item
            label={t('production.flowStatus.IPDWaitForAudit.IPDPass.form.title')}
            name='reviewComment'
            rules={[{ required: true, whitespace: true }]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
      <FlowStatusCloseButton buttonType={buttonType} refresh={refresh} production={production}></FlowStatusCloseButton>
    </>
  )
}

export const DevOnGoingActionButton: React.FC<ActionButtonBase> = ({ production, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const isSuperAdmin = viewer.isSuperAdmin
  const isTO = (production.roles || []).some((r) => r.role === ProductionRole.TO && r.email === viewer.email)
  const shouldDisplayTOButton = isTO || isSuperAdmin

  const [confirmVisible, setConfirmVisible] = React.useState(false)
  const [deliveryVisible, setDeliveryVisible] = React.useState(false)
  const stages = production.additions.filter((a) => a.type === 'Stage')
  const initStagePredictWorkload = Object.fromEntries(
    stages.map((s) => {
      let predictWorkload = 0
      try {
        predictWorkload = JSON.parse(s.value || '{}')?.predictWorkload || 0
      } catch (_) {
        // ignore
      }

      return [s.field, predictWorkload]
    })
  )

  const [stagePredictWorkload, setStagePredictWorkload] = React.useState(initStagePredictWorkload)

  const [confirmForm] = Form.useForm()
  const [deliveryForm] = Form.useForm()

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setConfirmVisible(false)
        setDeliveryVisible(false)
      },
      onSuccess() {
        refresh()
        setConfirmVisible(false)
        setDeliveryVisible(false)
      },
    }
  )

  const onValuesChange = (data: any) => {
    if (Object.keys(data).every((key) => key.startsWith('Stage.'))) {
      setStagePredictWorkload({ ...stagePredictWorkload, ...data })
    }
  }

  return (
    <>
      {!production.confirmedDeliveryTime && shouldDisplayTOButton && (
        <Button loading={loading} type={buttonType} size='small' onClick={() => setConfirmVisible(true)}>
          {t('production.flowStatus.DevOnGoing.TOConfirm.button')}
        </Button>
      )}
      {shouldDisplayTOButton && (
        <Button loading={loading} type={buttonType} size='small' onClick={() => setDeliveryVisible(true)}>
          {t('production.flowStatus.DevOnGoing.delivery.button')}
        </Button>
      )}
      <FlowStatusCloseButton buttonType={buttonType} refresh={refresh} production={production}></FlowStatusCloseButton>
      <Modal
        visible={confirmVisible}
        destroyOnClose
        onCancel={() => setConfirmVisible(false)}
        onOk={async () => {
          const values = await confirmForm.validateFields()
          const confirmedDeliveryTime = values.confirmedDeliveryTime.format()
          const predictWorkloadObj = pickBy(values, (_, key) => key.startsWith('Stage.'))

          run({
            ...omitBy(values, (_, key) => key.startsWith('Stage.')),
            predictWorkload: JSON.stringify(predictWorkloadObj),
            confirmedDeliveryTime,
            action: 'TOConfirm',
          })
        }}
        okButtonProps={{ loading }}
        cancelButtonProps={{ disabled: loading }}
      >
        <Form
          form={confirmForm}
          layout='vertical'
          onValuesChange={onValuesChange}
          initialValues={{
            standard: production.standard,
            ...initStagePredictWorkload,
          }}
        >
          {stages.map((stage, index) => (
            <Form.Item
              label={t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadStage', {
                index: index + 1,
              })}
              key={stage.id}
              name={`${stage.type}.${index}`}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} precision={0} />
            </Form.Item>
          ))}
          <div className='font-bold mt-2'>
            {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTotal')}:{' '}
            {(Object.values(stagePredictWorkload) as number[]).reduce((pre, current) => pre + current, 0)}
          </div>
          <Form.Item label={t('production.detail.expectDeliveryTime')}>
            <DatePicker
              picker='date'
              showToday
              showTime={false}
              value={
                moment(production.expectDeliveryTime).isValid() ? moment(production.expectDeliveryTime) : undefined
              }
            />
          </Form.Item>
          <Form.Item
            label={t('production.flowStatus.DevOnGoing.TOConfirm.form.confirmDeliveryTime.title')}
            name='confirmedDeliveryTime'
            rules={[{ required: true }]}
          >
            <DatePicker picker='date' showToday showTime={false} />
          </Form.Item>
          <Form.Item
            valuePropName='content'
            trigger='onChange'
            label={t('production.detail.standard')}
            name='standard'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('production.detail.standard')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor className={'editorBorder'} disableAutoSaveStyle />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        visible={deliveryVisible}
        destroyOnClose
        onCancel={() => setDeliveryVisible(false)}
        onOk={async () => {
          const values = await deliveryForm.validateFields()
          const actualDeliveryTime = values.actualDeliveryTime.format()
          run({ ...values, actualDeliveryTime, action: 'delivery' })
        }}
      >
        <Form form={deliveryForm} layout='vertical'>
          <Form.Item
            label={t('production.flowStatus.DevOnGoing.TOConfirm.form.actualDeliveryTime.title')}
            name='actualDeliveryTime'
            rules={[{ required: true }]}
          >
            <DatePicker picker='date' showToday showTime={false} />
          </Form.Item>
          <Form.Item
            label={t('production.flowStatus.DevOnGoing.delivery.form.title')}
            name='deliveryContent'
            rules={[{ required: true, whitespace: true }]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const ProductWaitForValidateActionButton: React.FC<ActionButtonBase> = ({ production, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const isSuperAdmin = viewer.isSuperAdmin
  const isProductManager = (production.roles || []).some(
    (r) => [ProductionRole.产品经理, ProductionRole.负责人].includes(r.role as any) && r.email === viewer.email
  )

  const [visible, setVisible] = React.useState(false)
  const [passVisible, setPassVisible] = React.useState(false)
  const [formAdditionalVisible, setFormAdditionalVisible] = React.useState({
    isMeetDeliverTarget: null,
    isMeetDeliverTime: null,
    isMeetWithinPredictResource: null,
  })
  const [form] = Form.useForm()
  const [rejectReason, setRejectReason] = React.useState<{ label: string; value: string }[]>([])

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/action`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
        setPassVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
        setPassVisible(false)
      },
    }
  )

  React.useEffect(() => {
    // 每次打开初始化 form
    if (visible || passVisible) {
      form.resetFields()
    }
  }, [visible, passVisible])

  if (!isProductManager && !isSuperAdmin) {
    return null
  }

  return (
    <>
      <Button loading={loading} type={buttonType} size='small' onClick={() => setPassVisible(true)}>
        {t('production.flowStatus.ProductWaitForValidate.pass.button')}
      </Button>
      <Button loading={loading} danger type={buttonType} size='small' onClick={() => setVisible(true)}>
        {t('production.flowStatus.ProductWaitForValidate.reject.button')}
      </Button>
      <Modal
        title={t('production.flowStatus.ProductWaitForValidate.pass.button')}
        visible={passVisible}
        destroyOnClose
        onCancel={() => setPassVisible(false)}
        onOk={async () => {
          const values = await form.validateFields()
          run({
            ...values,
            confirmedTargetCompleteTime: values.confirmedTargetCompleteTime.toISOString(),
            action: 'pass',
          })
        }}
        okButtonProps={{ loading }}
        cancelButtonProps={{ disabled: loading }}
      >
        <Form
          form={form}
          layout='vertical'
          onFieldsChange={(fields) => {
            const [field] = fields
            const fieldName = field?.name?.[0]
            if (fieldName && fieldName in formAdditionalVisible) {
              const draft = produce(formAdditionalVisible, (draft) => {
                draft[fieldName] = field.value
              })
              setFormAdditionalVisible(draft)
            }
          }}
        >
          <Form.Item
            rules={[{ required: true }]}
            label={t('production.flowStatus.ProductWaitForValidate.pass.form.targetTitle')}
            name='isMeetDeliverTarget'
          >
            <Radio.Group>
              <Radio value={true}>{t('yes')}</Radio>
              <Radio value={false}>{t('no')}</Radio>
            </Radio.Group>
          </Form.Item>
          {formAdditionalVisible.isMeetDeliverTarget === false && (
            <Form.Item
              rules={[{ required: true, whitespace: false }]}
              name='deliverTargetReason'
              label={t('production.flowStatus.ProductWaitForValidate.pass.form.targetFailedTitle')}
            >
              <Input.TextArea autoSize={{ minRows: 4 }} />
            </Form.Item>
          )}
          <Form.Item
            rules={[{ required: true }]}
            label={t('production.flowStatus.ProductWaitForValidate.pass.form.timeTitle')}
            name='isMeetDeliverTime'
          >
            <Radio.Group>
              <Radio value={true}>{t('yes')}</Radio>
              <Radio value={false}>{t('no')}</Radio>
            </Radio.Group>
          </Form.Item>
          {formAdditionalVisible.isMeetDeliverTime === false && (
            <Form.Item
              rules={[{ required: true, whitespace: false }]}
              name='deliverTimeReason'
              label={t('production.flowStatus.ProductWaitForValidate.pass.form.timeFailedTitle')}
            >
              <Input.TextArea autoSize={{ minRows: 4 }} />
            </Form.Item>
          )}
          <Form.Item
            rules={[{ required: true }]}
            label={t('production.flowStatus.ProductWaitForValidate.pass.form.predictResourceTitle')}
            name='isMeetWithinPredictResource'
          >
            <Radio.Group>
              <Radio value={true}>{t('yes')}</Radio>
              <Radio value={false}>{t('no')}</Radio>
            </Radio.Group>
          </Form.Item>
          {formAdditionalVisible.isMeetWithinPredictResource === false && (
            <Form.Item
              rules={[{ required: true, whitespace: false }]}
              name='predictResourceReason'
              label={t('production.flowStatus.ProductWaitForValidate.pass.form.predictResourceFailedTitle')}
            >
              <Input.TextArea autoSize={{ minRows: 4 }} />
            </Form.Item>
          )}
          <Form.Item
            name='confirmedTargetCompleteTime'
            rules={[{ required: true }]}
            label={t('production.flowStatus.ProductWaitForValidate.pass.form.confirmedTargetCompleteTimeTitle')}
            tooltip={t('production.flowStatus.ProductWaitForValidate.pass.form.confirmedTargetCompleteTimeTooltip')}
          >
            <DatePicker picker='date' showTime={false} />
          </Form.Item>
          <Form.Item label={t('production.detail.expectTargetCheckTime')}>
            <DatePicker
              picker='date'
              showTime={false}
              defaultValue={production.expectTargetCheckTime ? moment(production.expectTargetCheckTime) : undefined}
              disabled={true}
            ></DatePicker>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('production.flowStatus.ProductWaitForValidate.reject.button')}
        visible={visible}
        destroyOnClose
        onCancel={() => setVisible(false)}
        onOk={async () => {
          const values = await form.validateFields()
          if (!values.rejectReason) {
            message.error(t('production.flowStatus.ProductWaitForValidate.reject.form.errorMsg'))
            return
          }
          const rejectReason = (values.rejectReason || []).map((r: any) => {
            const key = Object.keys(r)[0] as keyof typeof ValidateNotPassReason
            return { name: ValidateNotPassReason[key], value: r[key] || '' }
          })

          run({ rejectReason, action: 'reject' })
        }}
        okButtonProps={{ danger: true }}
        okText={t('reject')}
      >
        <Form form={form} layout='vertical'>
          <Form.List name='rejectReason'>
            {(fields, { add, remove }) => {
              return (
                <>
                  <Form.Item required label={t('production.flowStatus.ProductWaitForValidate.reject.form.title')}>
                    <Select
                      labelInValue={true}
                      mode='multiple'
                      value={rejectReason.map((r) => r.value!)}
                      onChange={(_, options: any[]) => {
                        const [insertItem] = differenceBy(options, rejectReason, 'value')
                        const [removeItem] = differenceBy(rejectReason, options, 'value')
                        if (insertItem) {
                          add()
                        }
                        if (removeItem) {
                          const index = rejectReason.findIndex((p) => p.value === removeItem.value)
                          remove(index)
                        }
                        setRejectReason(options)
                      }}
                      options={Object.keys(ValidateNotPassReason).map((key) => ({
                        label: t(
                          `production.flowStatus.ProductWaitForValidate.reject.${
                            ValidateNotPassReason[key as keyof typeof ValidateNotPassReason]
                          }`
                        ),
                        value: ValidateNotPassReason[key as keyof typeof ValidateNotPassReason],
                      }))}
                    />
                  </Form.Item>
                  {fields.map(({ key, name, ...restField }, index) => {
                    return (
                      <Form.Item
                        key={key}
                        label={rejectReason[index].label}
                        name={[name, rejectReason[index].label]}
                        {...restField}
                      >
                        <Input.TextArea autoSize={{ maxRows: 4, minRows: 2 }}></Input.TextArea>
                      </Form.Item>
                    )
                  })}
                </>
              )
            }}
          </Form.List>
        </Form>
      </Modal>
    </>
  )
}

export const TrackingResultActionButton: React.FC<ActionButtonBase> = ({
  production,
  refresh,
  financialOperationAdmin,
  buttonType,
}) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const isFinancialOperationAdmin = financialOperationAdmin.includes(viewer.email)
  const isSuperAdmin = viewer.isSuperAdmin

  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const { run, loading } = useRequest(
    (data) => ({
      url: `/api/production/flow/result-tracking`,
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onError() {
        setVisible(false)
      },
      onSuccess() {
        refresh()
        setVisible(false)
      },
    }
  )

  if (!isFinancialOperationAdmin && !isSuperAdmin) {
    return null
  }

  return (
    <>
      <Button loading={loading} type={buttonType} size='small' onClick={() => setVisible(true)}>
        {t('production.flowStatus.TrackingResult.button')}
      </Button>
      <Modal
        title={t('production.flowStatus.TrackingResult.button')}
        visible={visible}
        destroyOnClose
        onCancel={() => setVisible(false)}
        onOk={async () => {
          const values = await form.validateFields()
          run(values)
        }}
        okText={t('save')}
        okButtonProps={{ loading }}
        cancelButtonProps={{ disabled: loading }}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            label={t('production.flowStatus.TrackingResult.form.title')}
            tooltip={{
              title: (
                <div>
                  1.项目是否实现立项时的业务目标
                  <br />
                  2.项目立项时确定的各可量化追踪的指标的实现情况
                </div>
              ),
            }}
            name='trackingResult'
            rules={[{ required: true, whitespace: true }]}
          >
            <Input.TextArea autoSize={{ maxRows: 4, minRows: 2 }}></Input.TextArea>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export const ProjectWaitForFinishActionButton: React.FC<ActionButtonBase> = ({ production, refresh, buttonType }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)

  const [visible, setVisible] = React.useState(false)

  const isRole = (production.roles || []).some((r) => r.email === viewer.email)
  const isSuperAdmin = viewer.isSuperAdmin

  const { run, loading } = useRequest(
    () => ({
      url: '/api/production/flow/action',
      method: 'post',
      data: {
        selfId: production.selfId,
        flowStatus: production.flowStatus,
      },
    }),
    {
      manual: true,
      onSuccess: async () => {
        setVisible(false)
        refresh()
      },
    }
  )

  if (!isRole && !isSuperAdmin) {
    return null
  }

  return (
    <>
      <Button type={buttonType} onClick={() => setVisible(true)} size='small' loading={loading}>
        {t('customer.flowStatus.ProjectWaitForFinish.button')}
      </Button>
      <Modal
        width={1000}
        title={t('customer.flowStatus.ProjectWaitForFinish.button')}
        okText={t('customer.flowStatus.ProjectWaitForFinish.popover.button')}
        okButtonProps={{ loading, danger: true }}
        cancelButtonProps={{ disabled: loading }}
        onOk={() => {
          run()
        }}
        onCancel={() => {
          setVisible(false)
        }}
        visible={visible}
      >
        <FinalActionResourceTable
          production={production}
          refresh={refresh}
          actionStatus={t('production.flowStatus.termination.finish')}
        />
      </Modal>
    </>
  )
}

const FinalActionResourceTable: React.FC<{
  production: Production
  refresh: () => void
  actionStatus: string
}> = ({ production, refresh, actionStatus }) => {
  const { t } = useTranslation()

  const { tableProps } = useAntdTable<PpRelation[], any, PpRelation[]>(
    {
      url: '/api/relations/getPpRelations',
      params: {
        parentSelfId: production.selfId,
        rootTypes: [ResourceType.PROJECT, ResourceType.PORTFOLIO],
        childTypes: [ResourceType.PROJECT, ResourceType.PORTFOLIO],
        fetchType: FetchType.fetchChild,
      },
    },
    {
      refreshDeps: [production.selfId],
      formatResult: (data) => ({ list: deleteChildren(data), total: 0 }),
    }
  )

  return (
    <>
      <div className='text-red-500 mb-4'>
        <div>{t('production.flowStatus.ProjectClosed.warning1')}</div>
        <div>{t('production.flowStatus.ProjectClosed.warning2')}</div>
        <div>
          {t('production.flowStatus.ProjectClosed.warning3', {
            status: actionStatus,
          })}
        </div>
      </div>
      <div className='max-h-[500px] overflow-y-auto'>
        <NormalResourceTable
          selfId={production.selfId}
          type={ResourceType.PRODUCTION}
          pageType={ResourceType.PRODUCTION}
          name={document.title}
          onSuccess={refresh}
          canDelete={false}
          canUpdate={false}
          {...omit(tableProps, 'pagination')}
          sticky={true}
        />
      </div>
    </>
  )
}
